import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { fmtRM } from "@/lib/api";

export const PageHeader = ({ title, description, actions, testId }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8" data-testid={testId || "page-header"}>
    <div>
      <h2 className="font-display text-2xl sm:text-3xl font-semibold text-foreground">{title}</h2>
      {description && <p className="text-sm text-muted-foreground mt-1 max-w-xl">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const StatCard = ({ icon: Icon, label, value, sub, accent = false, testId }) => (
  <div
    data-testid={testId}
    className={`rounded-lg border p-5 h-full lift ${accent ? "bg-primary text-primary-foreground border-primary" : "bg-card"}`}
  >
    <div className="flex items-center justify-between">
      <p className={`text-xs font-bold uppercase tracking-[0.15em] ${accent ? "text-secondary" : "text-muted-foreground"}`}>
        {label}
      </p>
      {Icon && <Icon className={`h-4 w-4 ${accent ? "text-secondary" : "text-secondary"}`} strokeWidth={2} />}
    </div>
    <p className="text-2xl sm:text-3xl font-bold font-display mt-2">{value}</p>
    {sub && <p className={`text-xs mt-1 ${accent ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{sub}</p>}
  </div>
);

const STATUS_STYLES = {
  active: "bg-[#D8F3DC] text-[#2D6A4F]",
  paid: "bg-[#D8F3DC] text-[#2D6A4F]",
  approved: "bg-[#D8F3DC] text-[#2D6A4F]",
  present: "bg-[#D8F3DC] text-[#2D6A4F]",
  on_time: "bg-[#D8F3DC] text-[#2D6A4F]",
  completed: "bg-[#D8F3DC] text-[#2D6A4F]",
  confirmed: "bg-[#D8F3DC] text-[#2D6A4F]",
  converted: "bg-[#D8F3DC] text-[#2D6A4F]",
  sent: "bg-[#D8F3DC] text-[#2D6A4F]",
  failed: "bg-[#FFEDEC] text-[#D90429]",
  pending: "bg-[#FFF8DC] text-[#B8860B]",
  draft: "bg-[#FFF8DC] text-[#B8860B]",
  submitted: "bg-[#FFF8DC] text-[#B8860B]",
  pending_teacher_response: "bg-[#FFF8DC] text-[#B8860B]",
  pending_parent_response: "bg-[#FFF8DC] text-[#B8860B]",
  under_discussion: "bg-[#FFF8DC] text-[#B8860B]",
  agreed_by_both_parties: "bg-[#D8F3DC] text-[#2D6A4F]",
  pending_admin_approval: "bg-[#E7F0FE] text-[#1D4ED8]",
  awaiting_teacher_proposal: "bg-[#E7F0FE] text-[#1D4ED8]",
  pending_direct_discussion: "bg-[#FFF3E0] text-[#B45309]",
  pending_evidence: "bg-[#FFF3E0] text-[#B45309]",
  admin_exception: "bg-[#FFEDEC] text-[#D90429]",
  quota_exceeded: "bg-[#FFEDEC] text-[#D90429]",
  fully_replaced: "bg-[#D8F3DC] text-[#2D6A4F]",
  partially_replaced: "bg-[#FFF3E0] text-[#B45309]",
  auto_confirmed: "bg-[#D8F3DC] text-[#2D6A4F]",
  acknowledged: "bg-[#D8F3DC] text-[#2D6A4F]",
  not_acknowledged: "bg-[#FFEDEC] text-[#D90429]",
  rescheduled: "bg-[#E7F0FE] text-[#1D4ED8]",
  partial: "bg-[#FFF8DC] text-[#B8860B]",
  unpaid: "bg-[#FFF8DC] text-[#B8860B]",
  new: "bg-[#FFF8DC] text-[#B8860B]",
  contacted: "bg-[#FFF8DC] text-[#B8860B]",
  excused: "bg-[#FFF8DC] text-[#B8860B]",
  overdue: "bg-[#FFEDEC] text-[#D90429]",
  rejected: "bg-[#FFEDEC] text-[#D90429]",
  late: "bg-[#FFEDEC] text-[#D90429]",
  absent: "bg-[#FFEDEC] text-[#D90429]",
  dropout: "bg-[#FFEDEC] text-[#D90429]",
  cancelled: "bg-[#FFEDEC] text-[#D90429]",
  expired: "bg-[#FFEDEC] text-[#D90429]",
  inactive: "bg-muted text-muted-foreground",
  superseded: "bg-muted text-muted-foreground",
  closed: "bg-muted text-muted-foreground",
  hidden: "bg-muted text-muted-foreground",
  trial_booked: "bg-[#E7F0FE] text-[#1D4ED8]",
  enrolled: "bg-[#D8F3DC] text-[#2D6A4F]",
  in_progress: "bg-[#E7F0FE] text-[#1D4ED8]",
  resolved: "bg-[#D8F3DC] text-[#2D6A4F]",
  open: "bg-[#FFF8DC] text-[#B8860B]",
  public: "bg-[#D8F3DC] text-[#2D6A4F]",
  internal_only: "bg-[#FFEDEC] text-[#D90429]",
  needs_follow_up: "bg-[#FFF8DC] text-[#B8860B]",
  pending_admin_review: "bg-[#FFF8DC] text-[#B8860B]",
  answered: "bg-[#D8F3DC] text-[#2D6A4F]",
  urgent: "bg-[#FFEDEC] text-[#D90429]",
  high: "bg-[#FFEDEC] text-[#D90429]",
  medium: "bg-[#FFF8DC] text-[#B8860B]",
  low: "bg-muted text-muted-foreground",
};

export const StatusBadge = ({ status, testId }) => (
  <Badge
    data-testid={testId}
    variant="outline"
    className={`rounded-full border-0 font-semibold capitalize ${STATUS_STYLES[status] || "bg-muted text-muted-foreground"}`}
  >
    {String(status || "").replace(/_/g, " ")}
  </Badge>
);

export const Money = ({ value, className = "" }) => (
  <span className={`font-mono text-sm ${className}`}>{fmtRM(value)}</span>
);

export const Spinner = ({ full = false }) =>
  full ? (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="loading-spinner">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  ) : (
    <Loader2 className="h-5 w-5 animate-spin text-primary" />
  );

export const EmptyState = ({ text = "Nothing here yet." }) => (
  <div className="text-center py-12 text-muted-foreground text-sm border border-dashed rounded-lg" data-testid="empty-state">
    {text}
  </div>
);

/* Reusable dashboard queue card: title + count pill + row list.
   Presentation-only — pass rows/renderers from the page (no business logic here). */
export const QueueCard = ({ title, icon: Icon, count, tone = "default", children, testId }) => {
  const tones = {
    default: "border",
    warning: "border border-[#B45309]/40",
    danger: "border border-destructive/40",
  };
  return (
    <div className={`rounded-lg bg-card p-5 ${tones[tone] || tones.default}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold flex items-center gap-1.5">
          {Icon && <Icon className="h-4 w-4 text-secondary" />}{title}
        </p>
        {count != null && (
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${count > 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
            {count}
          </span>
        )}
      </div>
      {children}
    </div>
  );
};

/* Reusable compact row for queue cards */
export const QueueRow = ({ primary, secondary, right, to, testId }) => {
  const body = (
    <>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{primary}</p>
        {secondary && <p className="text-xs text-muted-foreground truncate">{secondary}</p>}
      </div>
      {right && <div className="shrink-0 flex items-center gap-2">{right}</div>}
    </>
  );
  const cls = "flex items-center gap-3 py-2 border-b last:border-0";
  return to ? (
    <a href={to} className={`${cls} hover:bg-muted/30 -mx-2 px-2 rounded transition-colors`} data-testid={testId}>{body}</a>
  ) : (
    <div className={cls} data-testid={testId}>{body}</div>
  );
};
